@if (workflow().locked) {
  <div
    class="border-b border-warning-200 bg-warning-50 px-2xl py-md text-sm text-warning-700 flex items-center gap-sm flex-shrink-0"
    role="status">
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="currentColor"
      class="flex-shrink-0">
      <path
        d="M7 .5A3.5 3.5 0 0 0 3.5 4v1.5H2A1.5 1.5 0 0 0 .5 7v5A1.5 1.5 0 0 0 2 13.5h10A1.5 1.5 0 0 0 13.5 12V7A1.5 1.5 0 0 0 12 5.5h-1.5V4A3.5 3.5 0 0 0 7 .5Zm-2 3.5a2 2 0 0 1 4 0v1.5H5V4Z" />
    </svg>
    {{ 'custom_workflow_locked_banner' | transloco }}
  </div>
}

<div class="relative flex flex-1 h-[calc(100vh-14rem)] overflow-hidden">
  <!-- Zoom toolbar (left overlay) -->
  <div
    class="absolute top-md start-3.5 z-20 flex items-center gap-x-sm rounded-lg border border-neutral-200 bg-white px-x-sm py-x-sm shadow-sm">
    <button
      type="button"
      class="flex items-center justify-center size-4xl rounded-md border-none bg-transparent cursor-pointer text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
      (click)="zoomOut()"
      title="Zoom out">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    </button>
    <span
      class="min-w-9 text-center text-xs2 font-semibold text-neutral-500 select-none cursor-default"
      >{{ zoomPercent() }}</span
    >
    <button
      type="button"
      class="flex items-center justify-center size-4xl rounded-md border-none bg-transparent cursor-pointer text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
      (click)="zoomIn()"
      title="Zoom in">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    </button>
    <button
      type="button"
      class="flex items-center justify-center size-4xl rounded-md border-none bg-transparent cursor-pointer text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
      (click)="resetZoom()"
      title="Reset zoom">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    </button>
    <div class="w-px h-lg bg-neutral-200 mx-0.5 flex-shrink-0"></div>
    <button
      type="button"
      class="flex items-center justify-center size-4xl rounded-md border-none bg-transparent cursor-pointer text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
      (click)="fitToScreen()"
      title="Fit to screen">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <path
          d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M3 16v3a2 2 0 0 0 2 2h3" />
      </svg>
    </button>
  </div>

  <!-- Canvas body (scrollable, dot pattern background) -->
  <div
    #canvasBody
    id="canvasBody"
    class="flex-1 min-w-0 overflow-auto bg-neutral-50 bg-[radial-gradient(circle,#d1d5db_1.5px,transparent_1.5px)] [background-size:1.5rem_1.5rem] pt-[4.5rem] px-2xl pb-6xl">
    <!-- Flow viewport (zoom transform target) -->
    <div
      class="inline-flex origin-top-left transition-transform duration-[150ms]"
      [style.transform]="'scale(' + zoomScale() + ')'">
      <div class="flex items-center" id="custom_workflow_flow_row">
        <!-- Initiation card -->
        @if (initiationCard(); as card) {
          <div
            [id]="'custom_workflow_step_box_' + card.step.id"
            class="flex-shrink-0 w-75 rounded-2.5 border-[1.5px] border-neutral-200 bg-neutral-50 shadow-sm overflow-hidden">
            <div
              class="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-neutral-100 bg-neutral-100">
              <span
                class="text-sm font-semibold text-neutral-500 truncate min-w-0 flex-1 leading-tight">
                {{ card.title }}
              </span>
              @if (card.reflectionsCount > 0) {
                <span
                  data-testid="reflection-count"
                  class="flex-shrink-0"
                  role="img"
                  [attr.aria-label]="
                    'reflection_count_badge'
                      | transloco: { count: card.reflectionsCount }
                  "
                  [attr.title]="
                    'reflection_count_badge'
                      | transloco: { count: card.reflectionsCount }
                  ">
                  <lib-badge
                    [label]="'' + card.reflectionsCount"
                    [badgeClass]="'px-sm border-none text-xs font-medium bg-primary-50 text-primary-700'" />
                </span>
              }
            </div>
            <div class="px-3.5 pt-md pb-3.5 flex flex-col gap-x-sm">
              <div
                class="flex items-start gap-x-sm text-xs text-neutral-500 leading-snug">
                <span class="truncate">
                  {{ 'custom_workflow_initiation_hint' | transloco }}
                </span>
              </div>
              <div
                class="flex items-start gap-x-sm text-xs text-neutral-500 leading-snug">
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  class="size-3 flex-shrink-0 mt-0.25">
                  <circle cx="6" cy="6" r="5" />
                  <path d="M6 3.5v2.75l1.5 1" />
                </svg>
                <span>
                  {{
                    'custom_workflow_sla_hours'
                      | transloco: { hours: card.step.sla }
                  }}
                </span>
              </div>
            </div>
          </div>
        }

        <!-- Draggable step cards -->
        <div
          cdkDropList
          cdkDropListOrientation="horizontal"
          [cdkDropListDisabled]="workflow().locked || readonly()"
          (cdkDropListDropped)="dropStep($event)"
          class="flex items-center">
          @for (card of stepCards(); track card.step.id; let i = $index) {
            <div
              class="flex items-center flex-shrink-0"
              cdkDrag
              [cdkDragDisabled]="
                workflow().locked || readonly() || reorderingStepId() !== null
              ">
              <ng-template cdkDragPlaceholder>
                <div
                  class="w-0.5 self-stretch rounded-full bg-primary-500 mx-sm"></div>
              </ng-template>
              @if (!workflow().locked && !readonly()) {
                <div class="flex items-center flex-shrink-0">
                  <div class="w-md h-0.5 bg-neutral-200 flex-shrink-0"></div>
                  <button
                    type="button"
                    [id]="'custom_workflow_insert_step_' + i"
                    class="flex-shrink-0 size-3xl rounded-full border-[1.5px] border-dashed border-neutral-300 bg-white text-neutral-400 flex items-center justify-center font-light text-base leading-none hover:border-primary-500 hover:text-primary-500 hover:bg-primary-50 transition-all"
                    [attr.aria-label]="
                      'custom_workflow_insert_step_label' | transloco
                    "
                    (click)="addStep(card.step.position)">
                    +
                  </button>
                  <div class="w-md h-0.5 bg-neutral-200 flex-shrink-0"></div>
                </div>
              } @else {
                <div
                  class="flex items-center flex-shrink-0 px-2.5 text-neutral-300">
                  <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                    <path
                      d="M1 6h16M13 2l4 4-4 4"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
              }

              <!-- Step card (rich design) -->
              <div
                class="w-75 flex-shrink-0 rounded-xl bg-white border-[1.5px] shadow-sm overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-primary-500"
                [class.opacity-50]="reorderingStepId() === card.step.id"
                [class.border-neutral-200]="
                  !card.hasError && selectedStepId() !== card.step.id
                "
                [class.border-primary-500]="
                  !card.hasError && selectedStepId() === card.step.id
                "
                [class.border-destructive-500]="card.hasError"
                [id]="'custom_workflow_step_box_' + card.step.id"
                (click)="open(card.step.id)">
                <div
                  class="flex items-center gap-2.5 px-3.5 py-2.5 border-b"
                  [class.bg-primary-50]="!card.hasError"
                  [class.border-primary-100]="!card.hasError"
                  [class.bg-destructive-50]="card.hasError"
                  [class.border-destructive-100]="card.hasError">
                  <div
                    class="flex items-center justify-center h-7.5 w-auto rounded-lg flex-shrink-0"
                    [class.cursor-grab]="!workflow().locked">
                    <svg
                      width="12"
                      height="14"
                      viewBox="0 0 12 14"
                      fill="currentColor"
                      class="text-neutral-500">
                      <circle cx="3" cy="3" r="1.2" />
                      <circle cx="9" cy="3" r="1.2" />
                      <circle cx="3" cy="7" r="1.2" />
                      <circle cx="9" cy="7" r="1.2" />
                      <circle cx="3" cy="11" r="1.2" />
                      <circle cx="9" cy="11" r="1.2" />
                    </svg>
                  </div>
                  <span
                    class="text-sm font-semibold truncate min-w-0 flex-1 leading-tight"
                    [class.text-primary-600]="!card.hasError"
                    [class.text-destructive-700]="card.hasError"
                    >{{ i + 1 }} · {{ card.title }}</span
                  >
                  @if (card.reflectionsCount > 0) {
                    <span
                      data-testid="reflection-count"
                      class="flex-shrink-0"
                      role="img"
                      [attr.aria-label]="
                        'reflection_count_badge'
                          | transloco: { count: card.reflectionsCount }
                      "
                      [attr.title]="
                        'reflection_count_badge'
                          | transloco: { count: card.reflectionsCount }
                      ">
                      <lib-badge
                        [label]="'' + card.reflectionsCount"
                        [badgeClass]="'px-sm border-none text-xs font-medium bg-primary-50 text-primary-700'" />
                    </span>
                  }
                  <div class="flex gap-md">
                    @if (!readonly()) {
                      <button
                        type="button"
                        class="flex-shrink-0 flex items-center justify-center rounded border-none bg-transparent cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors"
                        (click)="
                          $event.stopPropagation(); duplicateStep(card.step.id)
                        ">
                        <lib-duplicate-icon [iconClass]="'size-4'" />
                      </button>
                    }
                    <button
                      type="button"
                      class="flex-shrink-0 flex items-center justify-center rounded border-none bg-transparent cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors"
                      (click)="$event.stopPropagation(); open(card.step.id)">
                      <lib-edit-icon [iconClass]="'size-4'" />
                    </button>
                  </div>
                </div>
                <div class="px-3.5 pt-md pb-3.5 flex flex-col gap-x-sm">
                  <div
                    class="flex items-start gap-x-sm text-xs leading-snug"
                    [class.text-neutral-500]="!card.hasError"
                    [class.text-destructive-700]="card.hasError">
                    <svg
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      class="size-3 flex-shrink-0 mt-0.25">
                      <circle cx="6" cy="3.5" r="2" />
                      <path d="M1 10c0-2.76 2.24-5 5-5s5 2.24 5 5" />
                    </svg>
                    <span class="truncate">{{ card.approver }}</span>
                  </div>
                  <div
                    class="flex items-start gap-x-sm text-xs text-neutral-500 leading-snug">
                    <svg
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      class="size-3 flex-shrink-0 mt-0.25">
                      <circle cx="6" cy="6" r="5" />
                      <path d="M6 3.5v2.75l1.5 1" />
                    </svg>
                    <span>{{
                      'custom_workflow_sla_hours'
                        | transloco: { hours: card.step.sla }
                    }}</span>
                  </div>
                  @if (card.hasError) {
                    <span
                      class="mt-x-sm self-start inline-flex items-center px-sm py-0.5 rounded-full text-xs2 font-medium bg-destructive-100 text-destructive-700"
                      >{{ 'custom_workflow_fix_required' | transloco }}</span
                    >
                  }
                </div>
              </div>
            </div>
          }
        </div>

        @if (!workflow().locked && !readonly()) {
          <div class="flex items-center flex-shrink-0">
            <div class="w-md h-0.5 bg-neutral-200 flex-shrink-0"></div>
            <button
              type="button"
              [id]="'custom_workflow_insert_step_' + stepCards().length"
              class="flex-shrink-0 size-3xl rounded-full border-[1.5px] border-dashed border-neutral-300 bg-white text-neutral-400 flex items-center justify-center font-light text-base leading-none hover:border-primary-500 hover:text-primary-500 hover:bg-primary-50 transition-all"
              [attr.aria-label]="
                'custom_workflow_insert_step_label' | transloco
              "
              (click)="addStep(nextPosition() - 1)">
              +
            </button>
            <div class="w-md h-0.5 bg-neutral-200 flex-shrink-0"></div>
          </div>
          <div
            id="custom_workflow_add_step"
            class="w-[150px] flex-shrink-0 self-stretch flex flex-col items-center justify-center gap-sm rounded-xl border-[1.5px] border-dashed bg-white text-neutral-500 text-sm font-medium cursor-pointer transition-all p-xl text-center hover:border-primary-500 hover:text-primary-500 hover:bg-primary-50"
            [class.border-neutral-300]="!boxErrors().noSteps"
            [class.border-destructive-500]="boxErrors().noSteps"
            (click)="addStep(nextPosition())">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle
                cx="11"
                cy="11"
                r="10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-dasharray="4 3" />
              <path
                d="M11 7v8M7 11h8"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round" />
            </svg>
            + {{ 'custom_workflow_add_step_button' | transloco }}
          </div>
        }
      </div>
    </div>
  </div>

  <!-- Inline step panel (slides in from right) -->
  <div
    class="flex-shrink-0 bg-white border-s border-neutral-200 flex flex-col overflow-hidden transition-[width] duration-[220ms]"
    [class.w-160]="drawer()?.kind === DrawerKind.Step"
    [class.w-0]="drawer()?.kind !== DrawerKind.Step">
    @if (selectedStep(); as step) {
      <div class="w-160 flex flex-col h-full">
        <header
          class="flex items-center justify-between px-lg py-lg border-b border-neutral-200 flex-shrink-0">
          <h2 class="text-sm font-semibold text-neutral-900">
            {{ selectedStepTitle() }}
          </h2>
          <button
            type="button"
            id="custom_workflow_drawer_close"
            class="flex items-center justify-center size-3xl rounded border-none bg-transparent cursor-pointer text-neutral-500 hover:bg-neutral-100 transition-colors"
            aria-label="close"
            (click)="requestClose()">
            ✕
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-lg">
          <lib-custom-workflow-step-editor
            #editor
            [workflow]="workflow()"
            [step]="step"
            [readonly]="readonly()"
            (changed)="changed.emit()"
            (deleted)="drawer.set(null); changed.emit()"
            (apiError)="apiError.emit($event)" />
        </div>
      </div>
    }
  </div>
</div>

<ng-template #discard>
  <div class="text-center font-medium text-lg text-neutral-900 mb-sm">
    {{ 'discard_modal_title' | transloco }}
  </div>
  <div class="text-center text-sm text-neutral-500">
    {{ 'discard_modal_description' | transloco }}
  </div>
  <div class="flex flex-col gap-md mt-4xl">
    <button
      id="custom_workflow_keep_editing"
      class="px-2xl py-sm rounded-md text-white bg-primary-500 text-lg font-medium border-none cursor-pointer"
      (click)="keepEditing()">
      {{ 'custom_workflow_keep_editing_button' | transloco }}
    </button>
    <button
      id="custom_workflow_discard"
      class="px-2xl py-sm rounded-md text-neutral-700 bg-white text-lg font-medium border border-neutral-300 cursor-pointer"
      (click)="discardAndClose()">
      {{ 'discard_modal_discard_button' | transloco }}
    </button>
  </div>
</ng-template>
